import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Home, Plus, Pencil, Trash2, Loader2, TrendingUp, Landmark, Wallet, Sparkles, FileText, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import PropertyLoans from "@/components/PropertyLoans";

const emptyForm = {
  name: "", address: "", purchase_date: "", purchase_price: "", current_value: "",
  category_id: "none", fully_repaid: false, color: "#22293A",
  ownership_value_pct: "100", ownership_credit_pct: "100", acquisition_costs: "",
};

export default function RealEstate({ onChanged }) {
  const [props, setProps] = useState([]);
  const [patri, setPatri] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(null);
  const [notarial, setNotarial] = useState(null);
  const [notarialBusy, setNotarialBusy] = useState(false);
  const notarialRef = useRef();
  const [expanded, setExpanded] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/properties"), api.get("/properties/patrimoine")])
      .then(([a, b]) => { setProps(a.data); setPatri(b.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data.filter((c) => c.type !== "income")));
  }, []);
  const reload = () => { load(); onChanged && onChanged(); };

  const toggleExpand = (id) => setExpanded((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));

  const num = (v) => Number(v) || 0;
  const openNew = () => { setForm(emptyForm); setEditId(null); setNotarial(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, address: p.address || "", purchase_date: (p.purchase_date || "").slice(0, 10),
      purchase_price: p.purchase_price ? String(p.purchase_price) : "",
      current_value: p.current_value ? String(p.current_value) : "",
      category_id: p.category_id || "none", fully_repaid: !!p.fully_repaid, color: p.color || "#22293A",
      ownership_value_pct: String(p.ownership_value_pct ?? 100), ownership_credit_pct: String(p.ownership_credit_pct ?? 100),
      acquisition_costs: p.acquisition_costs ? String(p.acquisition_costs) : "",
    });
    setEditId(p.id); setNotarial(p.acquisition_breakdown || null); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom du bien requis"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, address: form.address, purchase_date: form.purchase_date,
        purchase_price: num(form.purchase_price), current_value: num(form.current_value),
        category_id: form.category_id === "none" ? null : form.category_id,
        fully_repaid: form.fully_repaid, color: form.color,
        ownership_value_pct: num(form.ownership_value_pct), ownership_credit_pct: num(form.ownership_credit_pct),
        acquisition_costs: num(form.acquisition_costs),
      };
      if (editId) await api.put(`/properties/${editId}`, payload);
      else await api.post("/properties", payload);
      toast.success(editId ? "Bien modifié" : "Bien ajouté");
      setOpen(false); reload();
    } catch { toast.error("Erreur lors de l'enregistrement"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce bien et tous ses emprunts ?")) return;
    await api.delete(`/properties/${id}`); toast.success("Bien supprimé"); reload();
  };

  const estimate = async (p) => {
    if (!p.purchase_date || !p.purchase_price) { toast.error("Renseignez date et prix d'achat d'abord."); return; }
    setEstimating(p.id);
    try {
      const { data } = await api.post(`/properties/${p.id}/estimate-inflation`);
      toast.success(`Valeur indexée estimée : ${fmtEuro(data.inflation_value)}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'estimation"); }
    finally { setEstimating(null); }
  };

  const handleNotarial = async (file) => {
    if (!file || !editId) return;
    setNotarialBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/properties/${editId}/import-notarial`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNotarial(data.breakdown);
      setForm((f) => ({ ...f, acquisition_costs: String(data.acquisition_costs) }));
      toast.success(`Frais d'acquisition détectés : ${fmtEuro(data.acquisition_costs)}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'analyse du décompte"); }
    finally { setNotarialBusy(false); }
  };

  const field = (key, label, type = "text", placeholder = "") => (
    <div className="space-y-1.5"><Label>{label}</Label>
      <Input type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder} data-testid={`property-${key}-input`} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="real-estate-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-xl tracking-tight flex items-center gap-2">
            <Landmark className="w-5 h-5 text-primary" />Patrimoine immobilier
          </h2>
          <p className="text-muted-foreground text-sm mt-0.5">Vos biens, leurs emprunts, le capital remboursé et votre patrimoine net (selon votre quote-part).</p>
        </div>
        <Button onClick={openNew} data-testid="add-property-button"><Plus className="w-4 h-4 mr-1.5" />Nouveau bien</Button>
      </div>

      {loading || !patri ? (
        <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4" data-testid="patri-value">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold"><Home className="w-3.5 h-3.5" />Valeur (votre part)</div>
              <div className="font-num text-xl mt-1.5">{fmtEuro(patri.total_value)}</div>
              {patri.total_inflation_value > 0 && (
                <div className="text-[11px] text-muted-foreground mt-1">Indexée inflation : <span className="font-num">{fmtEuro(patri.total_inflation_value)}</span></div>
              )}
            </Card>
            <Card className="p-4" data-testid="patri-paid">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold"><Wallet className="w-3.5 h-3.5" />Capital remboursé</div>
              <div className="font-num text-xl mt-1.5 text-emerald-600">{fmtEuro(patri.total_paid_principal)}</div>
            </Card>
            <Card className="p-4" data-testid="patri-remaining">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold"><Landmark className="w-3.5 h-3.5" />Capital restant dû</div>
              <div className="font-num text-xl mt-1.5 text-destructive">{fmtEuro(patri.total_remaining)}</div>
            </Card>
            <Card className="p-4 bg-primary/5 border-primary/20" data-testid="patri-net">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-primary font-semibold"><TrendingUp className="w-3.5 h-3.5" />Patrimoine net</div>
              <div className="font-num text-xl mt-1.5 text-primary font-semibold">{fmtEuro(patri.total_net_worth)}</div>
            </Card>
          </div>

          <Card className="p-5 border-border">
            {props.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Aucun bien. Ajoutez un bien immobilier pour suivre votre patrimoine.</p>
            ) : (
              <div className="space-y-3">
                {props.map((p) => {
                  const partial = (p.ownership_value_pct ?? 100) < 100 || (p.ownership_credit_pct ?? 100) < 100;
                  const isOpen = expanded.includes(p.id);
                  return (
                    <div key={p.id} className="rounded-xl border border-border p-4" data-testid={`property-row-${p.id}`}>
                      <div className="flex items-start gap-3">
                        <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: p.color }}>
                          <Home className="w-4 h-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                            {p.name}
                            {p.fully_repaid && <Badge variant="outline" className="border-emerald-300 text-emerald-600 text-[10px]">remboursé</Badge>}
                            {partial && <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-600">{p.ownership_value_pct}% val · {p.ownership_credit_pct}% crédit</Badge>}
                          </div>
                          {p.address && <div className="text-xs text-muted-foreground truncate">{p.address}</div>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => estimate(p)} disabled={estimating === p.id} title="Estimer avec inflation" data-testid={`estimate-inflation-${p.id}`}>{estimating === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}</Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-property-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(p.id)} data-testid={`delete-property-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pl-12">
                        <div><div className="text-[11px] text-muted-foreground uppercase tracking-wide">Valeur (part)</div><div className="font-num text-sm">{fmtEuro(p.my_value)}</div>{p.inflation_value > 0 && <div className="text-[11px] text-muted-foreground">infl. {fmtEuro(p.inflation_value)}{p.inflation_rate ? ` · ${p.inflation_rate}%/an` : ""}</div>}</div>
                        <div><div className="text-[11px] text-muted-foreground uppercase tracking-wide">Remboursé</div><div className="font-num text-sm text-emerald-600">{fmtEuro(p.my_paid_principal)}</div></div>
                        <div><div className="text-[11px] text-muted-foreground uppercase tracking-wide">Restant dû</div><div className="font-num text-sm text-destructive">{fmtEuro(p.my_remaining)}</div></div>
                        <div><div className="text-[11px] text-muted-foreground uppercase tracking-wide">Patrimoine net</div><div className="font-num text-sm font-semibold text-primary">{fmtEuro(p.net_worth)}</div></div>
                      </div>

                      {(p.total_property_cost > 0 || p.total_credit_cost > 0) && (
                        <div className="pl-12 mt-3 rounded-lg bg-secondary/40 p-3" data-testid={`property-cost-${p.id}`}>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Coût total du bien</div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
                            <div className="flex justify-between sm:block"><span className="text-muted-foreground">Prix d'achat</span> <span className="font-num">{fmtEuro(p.purchase_price)}</span></div>
                            <div className="flex justify-between sm:block"><span className="text-muted-foreground">Frais d'acquisition</span> <span className="font-num">{fmtEuro(p.acquisition_costs)}</span></div>
                            <div className="flex justify-between sm:block"><span className="text-muted-foreground">Total intérêts</span> <span className="font-num">{fmtEuro(p.total_interest_scheduled)}</span></div>
                            <div className="flex justify-between sm:block"><span className="text-muted-foreground">Frais crédit + assurance</span> <span className="font-num">{fmtEuro(p.total_credit_cost - p.total_interest_scheduled)}</span></div>
                          </div>
                          <div className="flex justify-between mt-2 pt-2 border-t border-border/60 text-sm font-semibold"><span>Coût total</span><span className="font-num">{fmtEuro(p.total_property_cost)}</span></div>
                        </div>
                      )}

                      {p.inflation_note && <div className="pl-12 mt-2 text-[11px] text-muted-foreground italic">{p.inflation_note}</div>}

                      <div className="pl-12 mt-2">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={() => toggleExpand(p.id)} data-testid={`toggle-loans-${p.id}`}>
                          {isOpen ? <ChevronDown className="w-3.5 h-3.5 mr-1" /> : <ChevronRight className="w-3.5 h-3.5 mr-1" />}
                          {p.loans_count > 0 ? `${p.loans_count} emprunt${p.loans_count > 1 ? "s" : ""}` : "Gérer les emprunts"}
                        </Button>
                        {isOpen && <PropertyLoans propertyId={p.id} categories={categories} onChange={reload} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Add / edit property */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="property-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifier le bien" : "Nouveau bien immobilier"}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            <div className="space-y-3">
              {field("name", "Nom du bien", "text", "Ex: Maison principale, Appartement locatif")}
              {field("address", "Adresse (pour l'estimation d'inflation)", "text", "Ex: Rue de la Loi 16, 1000 Bruxelles")}
              <div className="grid grid-cols-2 gap-3">{field("purchase_date", "Date d'achat", "date")}{field("purchase_price", "Prix d'achat (€)", "number", "0,00")}</div>
              {field("current_value", "Valeur actuelle (€)", "number", "0,00")}
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Quote-part de propriété</div>
              <div className="grid grid-cols-2 gap-3">{field("ownership_value_pct", "% sur la valeur", "number", "100")}{field("ownership_credit_pct", "% sur le crédit", "number", "100")}</div>
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Frais d'acquisition</div>
              {field("acquisition_costs", "Total frais d'acquisition (€)", "number", "0,00")}
              {editId ? (
                <div className="rounded-lg border border-dashed border-border p-3">
                  <p className="text-xs text-muted-foreground mb-2">Importez le décompte notarial (PDF/image) — l'IA extrait les droits d'enregistrement, honoraires, TVA…</p>
                  <input ref={notarialRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => handleNotarial(e.target.files?.[0])} data-testid="notarial-file-input" />
                  <Button variant="outline" size="sm" disabled={notarialBusy} onClick={() => notarialRef.current?.click()} data-testid="notarial-upload-button">
                    {notarialBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}Analyser un décompte notarial
                  </Button>
                  {notarial && notarial.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="notarial-breakdown">
                      {notarial.map((b, i) => (
                        <div key={i} className="flex justify-between text-xs"><span className="text-muted-foreground">{b.label}</span><span className="font-num">{fmtEuro(b.amount)}</span></div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">Enregistrez d'abord le bien pour importer le décompte notarial et ajouter des emprunts.</p>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-border pt-4">
              <Checkbox id="fully-repaid" checked={form.fully_repaid} onCheckedChange={(v) => setForm({ ...form, fully_repaid: !!v })} data-testid="property-repaid-checkbox" />
              <Label htmlFor="fully-repaid" className="font-normal text-sm">Bien entièrement remboursé (aucun emprunt en cours)</Label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Fermer</Button><Button onClick={save} disabled={saving} data-testid="save-property-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
